// 26 ADM-080: the approval queue shows a group called "Fair play numbers". Who is in it is the spec's
// own lists, kept here word for word so a test can check them (T-ADM-57, T-ADM-68):
// - ADM-080: every ⚖ key under fairplay., training., potential., caddie., limits., biz. and family.;
// - ADM-095 point 1: every ⚖ key under shot.thought., focus.routine_saving_max,
//   equipment.ball_misfit_max_pct, ai.ball_fit_min, and the ⚖ keys under skills., junior_skills.,
//   world_juniors., junior_events. and family_pairs.;
// - ADM-103 point 9: the ⚖ keys under tech. (and biz.research., inside biz.);
// - ADM-104 point 4: study.knowledge_cap, study.sim_knowledge_cap,
//   reads.notes_min_course_knowledge_with_book, caddie.book_fee_coins, sim.gate.study_max_shots and the
//   five lines of 08 GOL-180 (ai.away_course_knowledge.*);
// - ADM-107 point 1: the keys it names one by one, the ⚖ habit and jet-lag keys of 09 and 07, and a
//   proposed anchor of level.handicap_anchors.
// The group is how the queue is shown. It is never the protection: every ⚖ key, in the group or
// not, takes effect only once an owner approves it, and the server enforces that (05 DAT-T08).

/** Every ⚖ key under one of these is in the group (ADM-080, ADM-095, ADM-103). */
export const FAIR_FAMILIES_MARKED = [
  'fairplay.',
  'training.',
  'potential.',
  'caddie.',
  'limits.',
  'biz.',
  'family.',
  'shot.thought.',
  'skills.',
  'junior_skills.',
  'world_juniors.',
  'junior_events.',
  'family_pairs.',
  'tech.',
  'habits.',
  'jetlag.',
] as const;

/** Every key under one of these is in the group, as its rule names the family (ADM-104, ADM-107). */
export const FAIR_FAMILIES_NAMED = [
  'ai.away_course_knowledge.',
  'rating.neutral.',
  'event_setup.',
] as const;

/** Keys the rules name one by one (ADM-095 point 1, ADM-104 point 4, ADM-107 point 1). */
export const FAIR_KEYS_NAMED: ReadonlySet<string> = new Set([
  'focus.routine_saving_max',
  'equipment.ball_misfit_max_pct',
  'ai.ball_fit_min',
  'study.knowledge_cap',
  'study.sim_knowledge_cap',
  'reads.notes_min_course_knowledge_with_book',
  'caddie.book_fee_coins',
  'sim.gate.study_max_shots',
  'travel.junior_factor',
  'travel.tour_event_coins',
  'prices.junior_factor',
  'niggle.ease_off_load_mult',
  'caddie.book_min_level',
  'field.junior_club_min',
  'clubs.weekly_open_comps_min',
  'clubs.insolvency_caretaker_weeks',
  'clubs.hole_in_one_treat_coins',
  'safety.u18_starter_groups_enabled',
  'sim.kit.ball_wind_span',
  'sim.kit.wet_hold_span',
  'sim.kit.grip_wet_span',
  'sim.kit.carry_saving_span',
  'sim.kit.breathe_span',
  'sim.gate.study_min_effect_shots',
  'sim.gate.study_played_max_shots',
  'level.handicap_anchors',
]);

/** True when a request for this key is listed under "Fair play numbers" (ADM-080). */
export function inFairPlayGroup(key: string, fair: boolean): boolean {
  if (FAIR_KEYS_NAMED.has(key)) return true;
  if (FAIR_FAMILIES_NAMED.some((p) => key.startsWith(p))) return true;
  return fair && FAIR_FAMILIES_MARKED.some((p) => key.startsWith(p));
}

/** The twelve thoughts (10 SIM-251), in the order the option-worth pass names them. */
export const THOUGHTS = [
  'smooth_tempo',
  'finish_the_swing',
  'small_target',
  'stay_tall',
  'soft_hands',
  'land_on_a_spot',
  'brush_the_grass',
  'keep_turning',
  'head_still',
  'smooth_stroke',
  'roll_it_over_a_spot',
  'listen_for_it',
] as const;

/** A share as a percentage with one decimal place: 0.034 → "3.4%". */
export function pct(share: number): string {
  return `${(Math.round(share * 1000) / 10).toFixed(1)}%`;
}

/**
 * The sixteen named proofs of 31 TST-180 (Blueprint 2, decision record 0102: `better_inputs` in place of
 * `play_styles`, and `decision_gate` and `honest_explanations` new), by key, as the console names them,
 * in ops.named_proofs()'s order. The runs of the retired `play_styles` stay stored as history and are
 * not listed.
 */
export const PROOF_NAMES: Readonly<Record<string, string>> = {
  calibration: 'Calibration',
  caddie_limit: 'Caddie limit',
  better_inputs: 'Better inputs never worse',
  judgement: 'Judgement and the twelve thoughts',
  information: 'Information',
  server_truth: 'Truth stays on the server',
  pressure_budget: 'Pressure budget',
  decision_gate: 'The decision gate',
  honest_explanations: 'Honest explanations',
  one_cap: 'The one cap',
  pretend_year: 'The pretend year',
  effects: 'Effects',
  homegrown: 'Homegrown',
  family_enjoyment: 'Family enjoyment',
  study_limit: 'Study limit',
  kit_ceilings: 'Kit ceilings',
};

/** A thought's name as the screen shows it: smooth_tempo → "Smooth tempo". */
export function thoughtName(t: string): string {
  const s = t.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}
